import React, { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useCart } from '../cart/CartContext';
import { useCartStockSync } from '../cart/useCartStockSync';
import { useProducts } from '../useProducts';
import { findCartPersonalizationIssues } from '../checkout/cartPersonalizationValidation';
import { useSiteSettings } from '../SiteSettingsContext';
import { getMergedStoreCopy, fillCopyTemplate } from '../storeCopy';
import PublicImage from '../PublicImage';
import QtyStepper from '../checkout/QtyStepper';
import { formatINR } from '../checkout/formatINR';
import { lineMaxQty, cartLineKey } from '../cart/cartUtils';
import { productDetailPath } from '../productPaths';
import OrderCustomizationDisplay from '../jewelry/OrderCustomizationDisplay';
import FreeShippingNudge from './FreeShippingNudge';
import StorefrontEmptyState from './StorefrontEmptyState';
import LineItemRemoveButton from './LineItemRemoveButton';
import ChevronIcon from './icons/ChevronIcon';
import { IconCart } from './icons/StorefrontIcons';

export default function CartDrawer({ shippingRules = null }) {
  const { ready } = useAuth();
  const { lines, subtotal, itemCount, drawerOpen, closeDrawer, setQty, removeItem, cartLoading, serverCartEnabled } = useCart();
  const { products } = useProducts();
  const stockSyncEnabled = drawerOpen && lines.length > 0;
  const { stockNotice, stockLoading } = useCartStockSync({ enabled: stockSyncEnabled });
  const { settings: site } = useSiteSettings();
  const store = getMergedStoreCopy(site);
  const panelRef = useRef(null);

  const personalizationIssues = useMemo(
    () => findCartPersonalizationIssues(lines, products),
    [lines, products]
  );
  const checkoutBlocked = !ready || personalizationIssues.length > 0;
  const checkoutLabel = !ready ? store.cart_checkout_loading : store.cart_drawer_checkout;

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeDrawer();
    };
    document.addEventListener('keydown', onKey);
    document.body.classList.add('cart-drawer-open');
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('cart-drawer-open');
    };
  }, [drawerOpen, closeDrawer]);

  useEffect(() => {
    if (drawerOpen && panelRef.current) {
      panelRef.current.focus();
    }
  }, [drawerOpen]);

  if (!drawerOpen) return null;

  return (
    <div className="cart-drawer" role="presentation">
      <button type="button" className="cart-drawer__backdrop" aria-label="Close cart" onClick={closeDrawer} />
      <aside
        ref={panelRef}
        className="cart-drawer__panel"
        role="dialog"
        aria-modal="true"
        aria-label="Shopping bag"
        tabIndex={-1}
      >
        <header className="cart-drawer__head">
          <h2 className="cart-drawer__title">
            {fillCopyTemplate(store.cart_drawer_title, { count: itemCount })}
          </h2>
          <button type="button" className="btn-close cart-drawer__close" aria-label="Close" onClick={closeDrawer} />
        </header>

        {lines.length === 0 ? (
          <div className="cart-drawer__empty">
            <StorefrontEmptyState
              title={store.cart_empty_title || 'Your bag is empty'}
              text={store.cart_drawer_empty_lead}
            >
              <Link to="/shop" className="btn btn-primary rounded-pill w-100 store-cta" onClick={closeDrawer}>
                {store.cart_drawer_shop_button}
              </Link>
            </StorefrontEmptyState>
          </div>
        ) : (
          <>
            {cartLoading ? (
              <div className="cart-drawer__stock-sync px-3 py-2 small text-muted" aria-live="polite">
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
                {store.cart_sync_loading}
              </div>
            ) : null}
            {!cartLoading && serverCartEnabled === false ? (
              <div className="alert alert-warning py-2 small mx-3 mt-2 mb-0" role="status">
                {store.cart_server_sync_unavailable}
              </div>
            ) : null}
            {stockLoading ? (
              <div className="cart-drawer__stock-sync px-3 py-2 small text-muted" aria-live="polite">
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
                {store.cart_stock_sync_loading}
              </div>
            ) : null}
            {stockNotice ? (
              <div className="alert alert-warning py-2 small mx-3 mt-2 mb-0" role="status">
                {stockNotice}
              </div>
            ) : null}
            {personalizationIssues.length > 0 ? (
              <div className="alert alert-warning py-2 small mx-3 mt-2 mb-0" role="alert">
                <p className="mb-1 fw-semibold">{store.cart_personalization_title}</p>
                <p className="mb-2">{store.cart_drawer_personalization_lead}</p>
                <ul className="mb-0 ps-3">
                  {personalizationIssues.map((issue) => (
                    <li key={cartLineKey(issue.line.categorySlug, issue.line.productId, issue.line.customization)}>
                      <Link
                        to={productDetailPath({ category: issue.line.categorySlug, id: issue.line.productId })}
                        className="alert-link"
                        onClick={closeDrawer}
                      >
                        {issue.line.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <FreeShippingNudge subtotal={subtotal} rules={shippingRules} className="cart-drawer__shipping" />
            <ul className="cart-drawer__list list-unstyled">
              {lines.map((line) => {
                const maxQty = lineMaxQty(line.stockQty);
                return (
                  <li key={cartLineKey(line.categorySlug, line.productId, line.customization)} className="cart-drawer__line">
                    <Link
                      to={productDetailPath({ category: line.categorySlug, id: line.productId })}
                      className="cart-drawer__thumb-link"
                      onClick={closeDrawer}
                    >
                      {line.image ? (
                        <PublicImage src={line.image} alt="" className="cart-drawer__thumb" />
                      ) : (
                        <span className="cart-drawer__thumb cart-drawer__thumb--empty" aria-hidden />
                      )}
                    </Link>
                    <div className="cart-drawer__line-body">
                      <Link
                        to={productDetailPath({ category: line.categorySlug, id: line.productId })}
                        className="cart-drawer__name"
                        onClick={closeDrawer}
                      >
                        {line.name}
                      </Link>
                      {line.customization ? (
                        <p className="cart-drawer__personalization small text-muted mb-1">
                          <OrderCustomizationDisplay raw={line.customization} />
                        </p>
                      ) : null}
                      <p className="cart-drawer__price small mb-1">{formatINR(line.price)}</p>
                      <QtyStepper
                        size="sm"
                        value={line.qty}
                        min={1}
                        max={maxQty}
                        onChange={(n) => setQty(line.categorySlug, line.productId, n, line.customization)}
                        label={`Quantity for ${line.name}`}
                      />
                    </div>
                    <LineItemRemoveButton
                      className="cart-drawer__remove"
                      label={`Remove ${line.name}`}
                      onClick={() => removeItem(line.categorySlug, line.productId, line.customization)}
                    />
                  </li>
                );
              })}
            </ul>
            <footer className="cart-drawer__foot">
              <div className="cart-drawer__subtotal">
                <span>{store.cart_drawer_subtotal}</span>
                <strong>{formatINR(subtotal)}</strong>
              </div>
              {checkoutBlocked && personalizationIssues.length > 0 ? (
                <Link
                  to="/cart"
                  className="btn btn-primary btn-lg rounded-pill w-100 store-cta"
                  onClick={closeDrawer}
                >
                  {store.cart_drawer_fix_on_cart}
                </Link>
              ) : (
                <Link
                  to="/checkout"
                  className={`btn btn-primary btn-lg rounded-pill w-100 store-cta${!ready ? ' disabled' : ''}`}
                  onClick={(e) => {
                    if (!ready) e.preventDefault();
                    else closeDrawer();
                  }}
                  aria-disabled={!ready}
                >
                  {checkoutLabel}
                </Link>
              )}
              <Link to="/cart" className="cart-drawer__full-cart" onClick={closeDrawer}>
                <span className="cart-drawer__full-cart-icon" aria-hidden>
                  <IconCart width={16} height={16} />
                </span>
                <span className="cart-drawer__full-cart-copy">
                  <span className="cart-drawer__full-cart-label">{store.cart_drawer_view_bag}</span>
                  <span className="cart-drawer__full-cart-hint">{store.cart_drawer_edit_bag}</span>
                </span>
                <span className="cart-drawer__full-cart-chevron" aria-hidden>
                  <ChevronIcon direction="right" />
                </span>
              </Link>
            </footer>
          </>
        )}
      </aside>
    </div>
  );
}
